/**
 * Shared utilities for media search panel components
 */

/**
 * Format duration in seconds to MM:SS format
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Format file size in bytes to human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Determine image orientation based on dimensions
 */
function getImageOrientation(
  width?: number,
  height?: number,
): "landscape" | "portrait" | "square" {
  if (!width || !height) return "square";
  const ratio = width / height;
  if (ratio > 1.2) return "landscape";
  if (ratio < 0.8) return "portrait";
  return "square";
}

/**
 * Determine video orientation based on dimensions
 */
function getVideoOrientation(
  width?: number,
  height?: number,
): "landscape" | "portrait" | "square" {
  if (!width || !height) return "landscape"; // Videos are usually landscape by default
  const ratio = width / height;
  if (ratio > 1.2) return "landscape";
  if (ratio < 0.8) return "portrait";
  return "square";
}

/**
 * onError handler for <img> tags that retries loading up to 2 times
 * with increasing delays and cache-busting params.
 */
function handleImageRetry(
  e: React.SyntheticEvent<HTMLImageElement>,
  originalSrc: string,
): void {
  const img = e.currentTarget;
  const retryCount = Number(img.dataset.retry || 0);
  if (retryCount < 2) {
    img.dataset.retry = String(retryCount + 1);
    setTimeout(() => {
      img.src =
        originalSrc +
        (originalSrc.includes("?") ? "&" : "?") +
        `_r=${retryCount + 1}`;
    }, 1000 * (retryCount + 1));
  }
}

/**
 * Get provider home URL for attribution links.
 * Unsplash requires UTM params per API guidelines.
 */
function getProviderUrl(provider: string): string {
  switch (provider) {
    case "unsplash":
      return "https://unsplash.com?utm_source=optidev&utm_medium=referral";
    case "pexels":
      return "https://www.pexels.com";
    case "pixabay":
      return "https://pixabay.com";
    case "freesound":
      return "https://freesound.org";
    default:
      return "";
  }
}

/**
 * Get capitalized display name for a provider.
 */
function getProviderDisplayName(provider: string): string {
  switch (provider) {
    case "unsplash":
      return "Unsplash";
    case "pexels":
      return "Pexels";
    case "pixabay":
      return "Pixabay";
    case "freesound":
      return "Freesound";
    default:
      return provider;
  }
}

/**
 * Get author URL with UTM params for Unsplash (required by their API guidelines).
 */
function getAuthorUrl(provider: string, authorUrl: string): string {
  if (provider === "unsplash" && authorUrl) {
    const separator = authorUrl.includes("?") ? "&" : "?";
    return `${authorUrl}${separator}utm_source=optidev&utm_medium=referral`;
  }
  return authorUrl;
}

/**
 * Get provider-compliant attribution text.
 * Each provider has specific required format per their API guidelines:
 * - Unsplash: "Photo by {Author} on Unsplash"
 * - Pexels: "Photo/Video by {Author} on Pexels"
 * - Pixabay: "Image/Video by {Author} on Pixabay"
 * - Freesound: "{Author} on Freesound"
 */
function getAttributionText(
  provider: string,
  author: string,
  _mediaType: "image" | "video" | "audio" = "image",
): string {
  if (!author) return getProviderDisplayName(provider);

  return `${author} on ${getProviderDisplayName(provider)}`;
}

/**
 * Check if a license requires prominent attribution (CC-BY).
 * Freesound CC-BY sounds need a visible badge.
 */
function isAttributionRequired(license: string): boolean {
  if (!license) return false;
  const normalized = license.toLowerCase();
  return (
    normalized.includes("cc-by") ||
    normalized.includes("cc by") ||
    normalized.includes("creative commons attribution")
  );
}

export {
  formatDuration,
  formatFileSize,
  getImageOrientation,
  getVideoOrientation,
  handleImageRetry,
  getProviderUrl,
  getProviderDisplayName,
  getAuthorUrl,
  getAttributionText,
  isAttributionRequired,
};
